/**
 * Authentication Page Component
 * Handles user login and registration
 */
import React, { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import api from '../../services/api';
import authService from '../../services/authService';
import useDarkMode from '../../hooks/useDarkMode';
import ThemeToggleButton from '../common/ThemeToggleButton';
import { useAuth } from '../../contexts/AuthContext';
import { LogIn, Eye, GraduationCap, KeyRound, Lock, Mail, Phone, Shield, User } from 'lucide-react';
import edsuppLogoBoy from '../../assets/logo/edsupp-logo-boy.svg';
import edsuppLogoGirl from '../../assets/logo/edsupp-logo-girl.svg';

const AuthPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, login, loginWithGoogle, loginWithApple, loginWithMobileOtp, register, updateUser } = useAuth();
  const { isDarkMode, toggleDarkMode } = useDarkMode();
  const [isLogin, setIsLogin] = useState(() => {
    const mode = searchParams.get('mode');
    return mode !== 'signup' && mode !== 'register';
  });
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    mobile: '',
    password: '',
    role: 'parent',
    access_code: ''
  });
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [loginMethod, setLoginMethod] = useState('email');
  const [mobileOtp, setMobileOtp] = useState('');
  const [mobileOtpInfo, setMobileOtpInfo] = useState('');
  const [error, setError] = useState('');
  const passwordsMismatch = !isLogin && confirmPassword && formData.password !== confirmPassword;
  const [showAccessCodeModal, setShowAccessCodeModal] = useState(false);
  const [accessCodeInput, setAccessCodeInput] = useState('');
  const [accessCodeError, setAccessCodeError] = useState('');
  const [tempUserData, setTempUserData] = useState(null); // Store user data temporarily until access code verification
  const [showResendVerification, setShowResendVerification] = useState(false);
  const [resendSuccess, setResendSuccess] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [restoreLoading, setRestoreLoading] = useState(false);
  const [restoreSuccess, setRestoreSuccess] = useState('');
  const [googleReady, setGoogleReady] = useState(false);
  const [googleClientId, setGoogleClientId] = useState('');
  const [googleClientConfigured, setGoogleClientConfigured] = useState(false);
  const [googleButtonRendered, setGoogleButtonRendered] = useState(false);
  const [appleClientId, setAppleClientId] = useState('');
  const [appleReady, setAppleReady] = useState(false);
  const [showRoleSelectionModal, setShowRoleSelectionModal] = useState(false);
  const [pendingSocialUser, setPendingSocialUser] = useState(null);
  const [selectedRole, setSelectedRole] = useState('parent');
  const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
  const isOriginMismatchError = typeof error === 'string' && error.toLowerCase().includes('origin_mismatch');
  const googleButtonContainerRef = useRef(null);
  const isLoginRef = useRef(isLogin);
  const signupRoleRef = useRef(formData.role);
  const signupMobileRef = useRef(formData.mobile);
  const authLogos = [edsuppLogoBoy, edsuppLogoGirl];
  const [activeLogoIndex, setActiveLogoIndex] = useState(0);

  useEffect(() => {
    const mode = searchParams.get('mode');

    if (mode === 'signup' || mode === 'register') {
      setIsLogin(false);
    } else if (mode === 'login' || mode === 'signin') {
      setIsLogin(true);
    }
  }, [searchParams]);

  useEffect(() => {
    isLoginRef.current = isLogin;
    signupRoleRef.current = formData.role;
    signupMobileRef.current = formData.mobile;
  }, [isLogin, formData.role, formData.mobile]);

  useEffect(() => {
    const interval = setInterval(() => {
      setActiveLogoIndex((prevIndex) => (prevIndex + 1) % authLogos.length);
    }, 2500);

    return () => clearInterval(interval);
  }, [authLogos.length]);


  useEffect(() => {
    let mounted = true;

    const resolveClientId = async () => {
      const envClientId =
        process.env.REACT_APP_GOOGLE_WEB_CLIENT_ID ||
        process.env.VITE_GOOGLE_WEB_CLIENT_ID ||
        process.env.REACT_APP_GOOGLE_CLIENT_ID ||
        process.env.VITE_GOOGLE_CLIENT_ID ||
        '';
      if (envClientId) {
        return envClientId;
      }
      const fallbackEndpoints = [
        () => api.get('/auth/google-config'),
        () => fetch('/api/auth/google-config').then((response) => (
          response.ok ? response.json().then((data) => ({ data })) : Promise.reject(new Error('Google config not found'))
        )),
      ];

      for (const requestConfig of fallbackEndpoints) {
        try {
          const response = await requestConfig();
          const clientId = response.data?.client_id || '';
          if (clientId) {
            return clientId;
          }
        } catch (err) {
          // Keep trying remaining endpoints
        }
      }

      return '';
    };

    const initGoogleSdk = async () => {
      const resolvedClientId = await resolveClientId();
      if (!mounted) return;

      setGoogleClientId(resolvedClientId);
      setGoogleClientConfigured(Boolean(resolvedClientId));
      if (!resolvedClientId) {
        setGoogleReady(false);
        return;
      }

      const updateGoogleReady = () => {
        const googleSdkLoaded = Boolean(window.google?.accounts?.id);
        if (mounted) {
          setGoogleReady(googleSdkLoaded);
        }
        return googleSdkLoaded;
      };

      if (updateGoogleReady()) {
        return;
      }

      const existingScript = document.querySelector('script[src="https://accounts.google.com/gsi/client"]');
      const script = existingScript || document.createElement('script');
      if (!existingScript) {
        script.src = 'https://accounts.google.com/gsi/client';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
      }

      const onLoad = () => updateGoogleReady();
      script.addEventListener('load', onLoad);

      const fallbackTimer = setInterval(() => {
        if (updateGoogleReady()) {
          clearInterval(fallbackTimer);
        }
      }, 400);

      return () => {
        script.removeEventListener('load', onLoad);
        clearInterval(fallbackTimer);
      };
    };

    let cleanup;
    initGoogleSdk().then((fn) => {
      cleanup = fn;
    });

    return () => {
      mounted = false;
      if (cleanup) cleanup();
    };
  }, []);

  useEffect(() => {
    let mounted = true;

    const resolveAppleClientId = async () => {
      const envClientId =
        process.env.REACT_APP_APPLE_CLIENT_ID ||
        process.env.VITE_APPLE_CLIENT_ID ||
        '';
      if (envClientId) return envClientId;

      const fallbackEndpoints = [
        () => api.get('/auth/apple-config'),
        () => fetch('/api/auth/apple-config').then((response) => (
          response.ok ? response.json().then((data) => ({ data })) : Promise.reject(new Error('Apple config not found'))
        )),
      ];

      for (const requestConfig of fallbackEndpoints) {
        try {
          const response = await requestConfig();
          const clientId = response.data?.client_id || '';
          if (clientId) return clientId;
        } catch (_err) {
          // Keep trying remaining endpoints
        }
      }

      return '';
    };

    const initAppleSdk = async () => {
      const resolvedClientId = await resolveAppleClientId();
      if (!mounted) return;

      setAppleClientId(resolvedClientId);
      if (!resolvedClientId) {
        setAppleReady(false);
        return;
      }

      const configureApple = () => {
        if (!window.AppleID?.auth) return false;
        window.AppleID.auth.init({
          clientId: resolvedClientId,
          scope: 'name email',
          redirectURI: window.location.origin,
          usePopup: true,
        });
        setAppleReady(true);
        return true;
      };

      if (configureApple()) return;

      const existingAppleScript = document.querySelector('script[src="https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js"]');
      const appleScript = existingAppleScript || document.createElement('script');
      if (!existingAppleScript) {
        appleScript.src = 'https://appleid.cdn-apple.com/appleauth/static/jsapi/appleid/1/en_US/appleid.auth.js';
        appleScript.async = true;
        document.head.appendChild(appleScript);
      }

      const onLoad = () => {
        if (!configureApple()) setAppleReady(false);
      };
      appleScript.addEventListener('load', onLoad);

      return () => appleScript.removeEventListener('load', onLoad);
    };

    let cleanup;
    initAppleSdk().then((fn) => {
      cleanup = fn;
    });

    return () => {
      mounted = false;
      if (cleanup) cleanup();
    };
  }, []);

  useEffect(() => {
    if (!googleReady || !googleClientId || !window.google?.accounts?.id || !googleButtonContainerRef.current) {
      return;
    }

    try {
      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: handleGoogleCredential,
        ux_mode: 'popup',
        auto_select: false,
        itp_support: true,
        use_fedcm_for_prompt: false,
        error_callback: (googleError) => {
          const reason = googleError?.type || googleError?.message || 'unknown_error';
          setLoading(false);
          if (reason === 'popup_failed_to_open') {
            setError('Safari blocked the Google popup. Please allow popups for this site and try again.');
            return;
          }
          setError(
            `Google sign-in failed (${reason}). If you see "origin_mismatch", add "${currentOrigin}" to Authorized JavaScript origins for this Web OAuth client in Google Cloud Console.`
          );
        },
      });

      window.google.accounts.id.renderButton(googleButtonContainerRef.current, {
        type: 'standard',
        size: 'large',
        text: 'continue_with',
        shape: 'pill',
        theme: 'outline',
        width: 360,
      });
      setGoogleButtonRendered(true);
    } catch (e) {
      setGoogleButtonRendered(false);
    }
  }, [googleReady, googleClientId, currentOrigin]);
  
  // Redirect if already logged in and access code is verified
  if (user) {
    return <Navigate to="/dashboard" />;
  }

  const activeLogo = authLogos[activeLogoIndex];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    if (!isLogin && formData.password !== confirmPassword) {
      setError('Passwords do not match');
      setLoading(false);
      return;
    }

    try {
      if (isLogin) {
        const result = loginMethod === 'mobile_otp'
          ? await loginWithMobileOtp(formData.mobile, mobileOtp)
          : await login(formData.email, formData.password);
        if (!result.success) {
          setError(result.error);
          
          // Check if error is due to unverified email
          if (result.error && result.error.includes('Email not verified')) {
            setShowResendVerification(true);
          } else {
            setShowResendVerification(false);
          }
        } else {
          // Success - check if coaching teacher (only they skip access code)
          console.log('Login successful!');
          if (['coaching_teacher', 'school_teacher', 'school_admin'].includes(result.user.role)) {
            // Coaching teachers, school teachers, and school admins go directly to dashboard without access code
            localStorage.setItem('accessCodeVerified', 'true');
            updateUser(result.user);
            navigate('/dashboard');
          } else {
            // Parent/child/super_admin/qb_admin users need to enter access code
            setTempUserData(result.user);
            setShowAccessCodeModal(true);
          }
        }
      } else {
        const result = await register(formData);
        if (!result.success) {
          setError(result.error);
        } else {
          // Success - redirect to dashboard (register() already sets user)
          console.log('Registration successful!');
          localStorage.setItem('accessCodeVerified', 'true');
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setError('An error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleSendLoginOtp = async () => {
    setError('');
    setMobileOtpInfo('');
    if (!formData.mobile || formData.mobile.length !== 10) {
      setError('Enter a valid 10-digit mobile number.');
      return;
    }
    try {
      await authService.sendMobileOtpForLogin(formData.mobile, 'whatsapp');
      setMobileOtpInfo('OTP sent successfully. Please check your mobile.');
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to send OTP');
    }
  };

  const handleAccessCodeVerification = async (e) => {
    e.preventDefault();
    if (!accessCodeInput.trim()) {
      setAccessCodeError('Please enter an access code');
      return;
    }

    setLoading(true);
    setAccessCodeError('');

    try {
      // Ensure token is in headers
      const token = localStorage.getItem('edsupp_token');
      if (!token) {
        setAccessCodeError('Session expired. Please login again.');
        setLoading(false);
        return;
      }
      
      const response = await axios.post('/auth/verify-access-code', {
        access_code: accessCodeInput
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.data.valid) {
        // Mark access code as verified
        localStorage.setItem('accessCodeVerified', 'true');
        
        // Store dashboard type and redirect. Persist only the active user/child
        // *id* — never names or the child PII object. The dashboard resolves the
        // full child record from the API by this id.
        localStorage.setItem('dashboardType', response.data.redirect_to);
        localStorage.setItem('activeUserId', response.data.user_id);

        // Reset any persisted dashboard mode when entering parent view
        if (response.data.redirect_to === 'parent') {
          localStorage.removeItem('dashboardMode');
        } else if (response.data.redirect_to === 'child') {
          localStorage.setItem('dashboardMode', 'child');
        }
        
        // Now set the user state to complete the login process
        if (tempUserData) {
          updateUser(tempUserData);
          
          // Redirect based on role
          if (tempUserData.role === 'qb_admin') {
            navigate('/admin/question-bank');
          } else if (tempUserData.role === 'super_admin') {
            navigate('/admin/dashboard');
          } else {
            navigate('/dashboard');
          }
        } else {
          navigate('/dashboard');
        }
      }
    } catch (error) {
      console.error('Access code verification error:', error);
      setAccessCodeError(error.response?.data?.detail || 'Invalid access code');
      
      // Clear auth completely so a page refresh doesn't bypass the access code check
      localStorage.removeItem('edsupp_token');
      localStorage.removeItem('accessCodeVerified');
      delete axios.defaults.headers.common['Authorization'];
      updateUser(null);
    } finally {
      setLoading(false);
    }
  };

  const canShowRestoreAction =
    isLogin &&
    typeof error === 'string' &&
    error.includes('Account scheduled for deletion');

  const handleRestoreAccountAccess = async () => {
    setRestoreLoading(true);
    setRestoreSuccess('');
    try {
      const response = await axios.post('/auth/restore-account-access', {
        email: formData.email,
        password: formData.password
      });
      setError('');
      setRestoreSuccess(response.data?.message || 'Account restored. You can sign in now.');
    } catch (restoreError) {
      setError(restoreError.response?.data?.detail || 'Failed to restore account access.');
    } finally {
      setRestoreLoading(false);
    }
  };

  const handleResendVerification = async () => {
    if (!formData.email) {
      setError('Please enter your email address');
      return;
    }

    setResendLoading(true);
    setResendSuccess('');
    setError('');

    try {
      const response = await axios.post('/auth/resend-verification', {
        email: formData.email
      });

      setResendSuccess(response.data.message);
      setShowResendVerification(false);
    } catch (error) {
      if (error.response?.status === 429) {
        setError(error.response?.data?.detail || 'Too many requests. Please try again later.');
      } else {
        setError(error.response?.data?.detail || 'Failed to resend verification email. Please try again.');
      }
    } finally {
      setResendLoading(false);
    }
  };

  const handleGoogleCredential = async (response) => {
    if (!response?.credential) {
      setError('Google sign-in was cancelled.');
      setLoading(false);
      return;
    }

    const isLoginMode = isLoginRef.current;
    const payload = {
      credential: response.credential,
      client_id: googleClientId || undefined,
      role: isLoginMode ? undefined : signupRoleRef.current,
      mobile: isLoginMode ? undefined : signupMobileRef.current
    };

    const result = await loginWithGoogle(payload);
    if (!result.success) {
      setError(result.error);
      setLoading(false);
      return;
    }

    if (isLoginMode && result.isNewUser) {
      setPendingSocialUser(result.user);
      setSelectedRole('parent');
      setShowRoleSelectionModal(true);
      setLoading(false);
      return;
    }

    if (!isLogin || ['coaching_teacher', 'school_teacher', 'school_admin'].includes(result.user.role)) {
      localStorage.setItem('accessCodeVerified', 'true');
      updateUser(result.user);
      navigate('/dashboard');
    } else {
      setTempUserData(result.user);
      setShowAccessCodeModal(true);
    }
    setLoading(false);
  };

  const handleAppleLogin = async () => {
    setError('');
    if (!appleReady || !appleClientId || !window.AppleID?.auth) {
      setError('Apple sign-in is still loading. Please try again in a moment.');
      return;
    }

    try {
      setLoading(true);
      const response = await window.AppleID.auth.signIn();
      const identityToken = response?.authorization?.id_token;
      if (!identityToken) {
        setError('Apple sign-in was cancelled.');
        setLoading(false);
        return;
      }
      const result = await loginWithApple({
        identity_token: identityToken,
        role: isLoginRef.current ? undefined : signupRoleRef.current,
        mobile: isLoginRef.current ? undefined : signupMobileRef.current
      });
      if (!result.success) {
        setError(result.error);
        setLoading(false);
        return;
      }
      if (isLoginRef.current && result.isNewUser) {
        setPendingSocialUser(result.user);
        setSelectedRole('parent');
        setShowRoleSelectionModal(true);
        return;
      }
      if (!isLogin || ['coaching_teacher', 'school_teacher', 'school_admin'].includes(result.user.role)) {
        localStorage.setItem('accessCodeVerified', 'true');
        updateUser(result.user);
        navigate('/dashboard');
      } else {
        setTempUserData(result.user);
        setShowAccessCodeModal(true);
      }
    } catch (e) {
      setError('Apple sign-in failed to start.');
    } finally {
      setLoading(false);
    }
  };

  const handleRoleSelectionSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await authService.selectRole(selectedRole);
      const updatedUser = response.data?.user || pendingSocialUser;
      if (response.data?.access_token) {
        localStorage.setItem('edsupp_token', response.data.access_token);
        axios.defaults.headers.common['Authorization'] = `Bearer ${response.data.access_token}`;
      }
      setShowRoleSelectionModal(false);
      setPendingSocialUser(null);
      if (selectedRole === 'coaching_teacher') {
        localStorage.setItem('accessCodeVerified', 'true');
        updateUser(updatedUser);
        navigate('/dashboard');
      } else {
        setTempUserData(updatedUser);
        setShowAccessCodeModal(true);
      }
    } catch (_e) {
      setError('Failed to save role. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleLogin = () => {
    setError('');
    if (!googleClientId || !window.google?.accounts?.id || !googleButtonRendered) {
      setError('Google sign-in is not configured.');
      return;
    }

    try {
      setLoading(true);
      const googleButton =
        googleButtonContainerRef.current?.querySelector('[role="button"]') ||
        googleButtonContainerRef.current?.firstElementChild;

      if (!googleButton) {
        setLoading(false);
        setError('Google sign-in is still loading. Please click the Google button below in a moment.');
        return;
      }

      googleButton.click();
    } catch (e) {
      setLoading(false);
      setError('Google sign-in failed to start.');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 to-yellow-50 flex items-center justify-center py-8 px-4 sm:px-6 lg:px-8 dark:from-slate-900 dark:to-slate-950">
      <ThemeToggleButton
        isDarkMode={isDarkMode}
        onToggle={toggleDarkMode}
        testId="auth-theme-toggle"
        className="fixed right-4 top-4 z-50 border border-slate-200 bg-white/90 shadow-sm hover:bg-white dark:border-slate-700 dark:bg-slate-800 dark:hover:bg-slate-700"
      />
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <img 
            src={activeLogo} 
            alt="Edsupp Logo" 
            className="mx-auto h-24 w-24 rounded-lg bg-white/90 p-1"
          />
        </div>

        <div className="rounded-2xl border border-white/50 bg-white/95 shadow-xl shadow-purple-100/70 ring-1 ring-purple-100/80 backdrop-blur-sm px-5 py-6 sm:px-6 dark:border-slate-700 dark:bg-slate-800/95 dark:shadow-none dark:ring-slate-700/80">
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div className="text-center">
              {/*<div className="mx-auto mb-3 flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-3xl">
                {isLogin ? '👋' : '✨'}
              </div>*/}

              <h1 className="text-2xl font-bold text-slate-900 dark:text-white">
                {isLogin ? 'Welcome back' : 'Create your account'}
              </h1>

              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                {isLogin
                  ? 'Sign in to continue your learning journey.'
                  : 'Start your Edsupp journey in a few seconds.'}
              </p>
            </div>

            <div className="space-y-3">
              <div className="hidden" aria-hidden="true">
                <div ref={googleButtonContainerRef} />
              </div>

              <button
                type="button"
                onClick={handleGoogleLogin}
                className="flex w-full items-center justify-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-slate-700 shadow-sm transition hover:border-blue-300 hover:bg-blue-50 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-200 dark:hover:border-blue-500 dark:hover:bg-slate-700"
              >
                <svg width="20" height="20" viewBox="0 0 48 48" aria-hidden="true">
                  <path fill="#EA4335" d="M24 9.5c3.54 0 6.74 1.22 9.25 3.6l6.9-6.9C35.96 2.25 30.36 0 24 0 14.62 0 6.51 5.38 2.56 13.22l8.04 6.24C12.56 13.67 17.84 9.5 24 9.5z" />
                  <path fill="#4285F4" d="M46.5 24.55c0-1.64-.15-3.22-.43-4.73H24v8.95h12.65c-.55 2.98-2.23 5.5-4.76 7.2l7.32 5.67C43.53 37.4 46.5 31.5 46.5 24.55z" />
                  <path fill="#FBBC05" d="M10.6 28.54c-.51-1.49-.8-3.08-.8-4.72s.29-3.23.8-4.72l-8.04-6.24A23.9 23.9 0 0 0 0 23.82c0 3.86.92 7.51 2.56 10.76l8.04-6.04z" />
                  <path fill="#34A853" d="M24 48c6.36 0 11.71-2.1 15.61-5.71l-7.32-5.67c-2.03 1.36-4.64 2.16-8.29 2.16-6.16 0-11.44-4.17-13.4-9.86l-8.04 6.04C6.51 42.62 14.62 48 24 48z" />
                </svg>
                <span>Continue with Google</span>
              </button>

              {/*<button
                type="button"
                onClick={handleAppleLogin}
                className="flex w-full items-center justify-center gap-3 rounded-2xl bg-[#06143a] px-4 py-3 text-sm font-semibold text-white shadow-sm transition hover:opacity-95"
              >
                <span className="text-2xl leading-none"></span>
                <span>Sign in with Apple</span>
              </button>

              {!appleReady && (
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                  {appleClientId
                    ? 'Apple sign-in is loading. If it does not appear, refresh the page.'
                    : 'Apple sign-in unavailable. Missing Apple client ID configuration.'}
                </p>
              )}*/}

              {!googleReady && (
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                  {googleClientConfigured
                    ? 'Google sign-in is loading. If it does not appear, refresh the page.'
                    : 'Google sign-in unavailable. Missing Google client ID configuration.'}
                </p>
              )}

              {googleReady && !googleButtonRendered && (
                <p className="text-center text-xs text-slate-500 dark:text-slate-400">
                  Preparing Google sign-in…
                </p>
              )}
            </div>

            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-slate-200 dark:border-slate-700" />
              </div>
              <div className="relative flex justify-center text-xs">
                <span className="bg-white px-3 font-medium uppercase tracking-wide text-slate-400 dark:bg-slate-800 dark:text-slate-500">
                  {isLogin ? 'or continue with' : 'or register with email'}
                </span>
              </div>
            </div>

            <div className="space-y-4">
              {!isLogin && (
                <>
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Full Name
                    </label>
                    <div className="relative">
                      <User size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 dark:text-blue-400" />
                      <input
                        type="text"
                        required
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 pl-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                        placeholder="Enter your full name"
                        data-testid="name-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Role
                    </label>

                    <div className="relative">
                      <GraduationCap
                        size={18}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 dark:text-blue-400"
                      />

                      <select
                        value={formData.role}
                        onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                        className="w-full appearance-none rounded-2xl border border-slate-200 bg-white px-4 py-3 pl-10 pr-10 text-sm text-slate-700 outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                        data-testid="role-select"
                      >
                        <option value="parent">Parent</option>
                        <option value="coaching_teacher">Coaching Teacher</option>
                      </select>

                      <span className="pointer-events-none absolute right-4 top-1/2 -translate-y-1/2 text-slate-400">
                        ▼
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Mobile Number
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center rounded-l-2xl border border-r-0 border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                        +91
                      </span>
                      <input
                        type="tel"
                        value={formData.mobile}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mobile: e.target.value.replace(/\D/g, '').slice(0, 10),
                          })
                        }
                        className="w-full rounded-r-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                        placeholder="10-digit mobile number"
                        data-testid="mobile-input"
                      />
                    </div>
                  </div>
                </>
              )}

              {isLogin && (
                <div className="grid grid-cols-2 rounded-2xl bg-slate-100 p-1 text-sm font-semibold dark:bg-slate-900">
                  <button
                    type="button"
                    onClick={() => setLoginMethod('email')}
                    className={`rounded-xl py-2 transition ${
                      loginMethod === 'email'
                        ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-700 dark:text-blue-400'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    Email
                  </button>

                  <button
                    type="button"
                    onClick={() => setLoginMethod('mobile_otp')}
                    className={`rounded-xl py-2 transition ${
                      loginMethod === 'mobile_otp'
                        ? 'bg-white text-blue-700 shadow-sm dark:bg-slate-700 dark:text-blue-400'
                        : 'text-slate-500 hover:text-slate-700 dark:text-slate-400 dark:hover:text-slate-200'
                    }`}
                  >
                    Mobile OTP
                  </button>
                </div>
              )}

              {isLogin && loginMethod === 'mobile_otp' ? (
                <>
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Mobile Number
                    </label>
                    <div className="flex">
                      <span className="inline-flex items-center rounded-l-2xl border border-r-0 border-slate-200 bg-slate-50 px-3 text-sm font-medium text-slate-600 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-400">
                        +91
                      </span>
                      <input
                        type="tel"
                        required
                        value={formData.mobile}
                        onChange={(e) =>
                          setFormData({
                            ...formData,
                            mobile: e.target.value.replace(/\D/g, '').slice(0, 10),
                          })
                        }
                        className="w-full rounded-r-2xl border border-slate-200 px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                        placeholder="10-digit mobile number"
                      />
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleSendLoginOtp}
                    className="w-full rounded-2xl bg-blue-50 py-3 text-sm font-semibold text-blue-700 transition hover:bg-blue-100 dark:bg-blue-950/60 dark:text-blue-300 dark:hover:bg-blue-900/60"
                  >
                    Send OTP
                  </button>

                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                      OTP
                    </label>
                    <input
                      type="text"
                      required
                      value={mobileOtp}
                      onChange={(e) => setMobileOtp(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm tracking-widest outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                      placeholder="Enter OTP"
                    />
                  </div>

                  {mobileOtpInfo && (
                    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                      {mobileOtpInfo}
                    </div>
                  )}
                </>
              ) : (
                <>
                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Email Address
                    </label>
                    <div className="relative">
                      <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 dark:text-blue-400" />
                      <input
                        type="email"
                        required
                        value={formData.email}
                        onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 pl-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                        placeholder="you@example.com"
                        data-testid="email-input"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                      Password
                    </label>
                    <div className="relative">
                      <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 dark:text-blue-400" />
                      <input
                        type="password"
                        required
                        value={formData.password}
                        onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 pl-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                        placeholder="Enter password"
                        data-testid="password-input"
                      />
                    </div>

                    {isLogin && (
                      <div className="mt-2 text-right">
                        <a href="/forgot-password" className="text-xs font-semibold text-blue-600 hover:text-blue-700 dark:text-blue-400 dark:hover:text-blue-300">
                          Forgot Password?
                        </a>
                      </div>
                    )}
                  </div>
                </>
              )}

              {!isLogin && (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Confirm Password
                  </label>
                  <div className="relative">
                    <Lock size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 dark:text-blue-400" />
                    <input
                      type="password"
                      required
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 pl-10 text-sm outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                      placeholder="Re-enter password"
                      data-testid="confirm-password-input"
                    />
                  </div>

                  {passwordsMismatch && (
                    <p className="mt-1.5 text-sm font-medium text-red-600 dark:text-red-400" data-testid="password-mismatch">
                      Passwords do not match
                    </p>
                  )}
                </div>
              )}

              {!isLogin && formData.role === 'parent' && (
                <div>
                  <label className="mb-1.5 block text-sm font-semibold text-slate-700 dark:text-slate-300">
                    Parent Access Code
                  </label>
                  <div className="relative">
                    <KeyRound size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-blue-600 dark:text-blue-400" />
                    <input
                      type="password"
                      value={formData.access_code}
                      onChange={(e) => setFormData({ ...formData, access_code: e.target.value })}
                      placeholder="Set your 6-digit access code"
                      className="w-full rounded-2xl border border-slate-200 px-4 py-3 pl-10 text-sm tracking-widest outline-none transition focus:border-blue-500 focus:ring-4 focus:ring-blue-100 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-100 dark:focus:ring-blue-900/40"
                      pattern="[0-9]{6}"
                      minLength="6"
                      maxLength="6"
                      required
                      data-testid="access-code-input"
                    />
                  </div>
                  <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-400">
                    This code will be used to access Parent Dashboard after login.
                  </p>
                </div>
              )}

              {error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-center text-sm font-medium text-red-700 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300" data-testid="error-message">
                  {error}
                </div>
              )}

              {canShowRestoreAction && (
                <button
                  type="button"
                  onClick={handleRestoreAccountAccess}
                  disabled={restoreLoading || !formData.email || !formData.password}
                  className="w-full rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm font-semibold text-emerald-700 transition hover:bg-emerald-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300 dark:hover:bg-emerald-900/50"
                >
                  {restoreLoading ? 'Restoring...' : 'Restore Account Access'}
                </button>
              )}

              {restoreSuccess && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                  {restoreSuccess}
                </div>
              )}

              {resendSuccess && (
                <div className="rounded-2xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-center text-sm font-medium text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950/50 dark:text-emerald-300">
                  {resendSuccess}
                </div>
              )}

              {showResendVerification && isLogin && (
                <div className="rounded-2xl border border-blue-100 bg-blue-50 p-4 text-center dark:border-blue-900 dark:bg-blue-950/50">
                  <p className="mb-3 text-sm text-slate-600 dark:text-slate-300">
                    Your email is not verified. Please check your inbox for the verification link.
                  </p>

                  <button
                    type="button"
                    onClick={handleResendVerification}
                    disabled={resendLoading}
                    className="w-full rounded-xl bg-white px-4 py-2 text-sm font-semibold text-blue-700 shadow-sm transition hover:bg-blue-100 disabled:opacity-50 dark:bg-slate-800 dark:text-blue-300 dark:hover:bg-slate-700"
                  >
                    {resendLoading ? 'Sending...' : 'Resend Verification Email'}
                  </button>
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="w-full rounded-2xl bg-blue-600 px-5 py-3 text-base font-semibold text-white shadow-md shadow-blue-200 transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 disabled:shadow-none dark:shadow-none dark:disabled:bg-slate-700"
                data-testid="submit-btn"
              >
                <span className="flex items-center justify-center gap-2">
                  <LogIn size={18} />
                  {loading ? 'Processing...' : isLogin ? 'Sign In' : 'Create Account'}
                </span>
              </button>
            </div>
          </form>

          <div className="mt-6 text-center">
            <button
              onClick={() => {
                const nextIsLogin = !isLogin;
                setIsLogin(nextIsLogin);
                setSearchParams({ mode: nextIsLogin ? 'login' : 'signup' });
                setError('');
                setShowResendVerification(false);
                setResendSuccess('');
                setConfirmPassword('');
              }}
              className="text-blue-600 hover:text-blue-700 font-medium dark:text-blue-400 dark:hover:text-blue-300"
              data-testid="toggle-auth-btn"
            >
              {isLogin ? "Don't have an account? Sign up" : "Already have an account? Sign in"}
            </button>
          </div>
            <div className="mt-6 flex justify-center">
              <button
                type="button"
                onClick={() => navigate('/')}
                className="inline-flex items-center gap-2 rounded-2xl border border-blue-300 bg-white px-4 py-2 text-sm font-medium text-blue-600 shadow-sm transition-all duration-200 hover:bg-blue-50 hover:shadow-md dark:border-blue-800 dark:bg-slate-800 dark:text-blue-400 dark:hover:bg-slate-700"
              >
                <span className="text-sm leading-none" aria-hidden="true">
                  ←
                </span>

                <span>Back to Home Page</span>
              </button>
            </div>
        </div>
      </div>

      {/* Access Code Modal */}
      {showAccessCodeModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 px-3 py-4 sm:px-4 backdrop-blur-[2px]">
          <div className="w-full max-w-md overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-xl dark:border-slate-700 dark:bg-slate-800">
            <div className="bg-gradient-to-r from-blue-50 via-white to-orange-50 px-5 py-5 text-center sm:px-6 dark:from-slate-900 dark:via-slate-800 dark:to-slate-900">
              <h3 className="text-lg font-bold text-blue-500 sm:text-[1.75rem] dark:text-blue-400">Choose Your Profile</h3>
              <p className="mt-1.5 text-sm text-slate-500 sm:text-base dark:text-slate-400">Enter your access code</p>
            </div>

            <div className="space-y-3 bg-blue-50/50 px-4 py-4 sm:px-5 dark:bg-slate-900/50">
              <div className="flex items-start gap-3 rounded-xl border border-blue-100 bg-white p-3 dark:border-blue-900 dark:bg-slate-800">
                <div className="rounded-lg bg-blue-100 p-1.5 text-blue-500 dark:bg-blue-950 dark:text-blue-400">
                  <User className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-blue-500 sm:text-lg dark:text-blue-400">Parent Profile</p>
                  <p className="text-xs text-slate-600 sm:text-sm dark:text-slate-400">6-digit code (Default Code: 000000)</p>
                </div>
              </div>

              <div className="flex items-start gap-3 rounded-xl border border-purple-100 bg-white p-3 dark:border-purple-900 dark:bg-slate-800">
                <div className="rounded-lg bg-purple-100 p-1.5 text-purple-500 dark:bg-purple-950 dark:text-purple-400">
                  <GraduationCap className="h-5 w-5" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-purple-500 sm:text-lg dark:text-purple-400">Child Profile</p>
                  <p className="text-xs text-slate-600 sm:text-sm dark:text-slate-400">4-digit code to Child's Dashboard</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleAccessCodeVerification} className="space-y-3 bg-slate-50 px-4 py-4 sm:px-5 sm:py-5 dark:bg-slate-900">
              <label className="flex items-center gap-2 text-sm sm:text-base font-semibold text-blue-700 dark:text-blue-400">
                <KeyRound className="h-5 w-5 text-blue-600 dark:text-blue-400" />
                Access Code
              </label>

              <div className="relative">
                <input
                  type="password"
                  value={accessCodeInput}
                  onChange={(e) => setAccessCodeInput(e.target.value)}
                  placeholder={tempUserData?.role === 'super_admin' ? 'Enter Super Admin code' : 'Your Access Code (Default: 000000)'}
                  className="w-full rounded-xl border-2 border-blue-500 bg-white px-4 py-3 pr-20 text-center text-sm text-slate-700 placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-300 sm:text-lm dark:bg-slate-800 dark:text-slate-100 dark:focus:ring-blue-900/40"
                  required
                />
                <div className="pointer-events-none absolute inset-y-0 right-4 flex items-center gap-2 text-blue-600 dark:text-blue-400">
                  <Shield className="h-5 w-5" />
                  <Eye className="h-6 w-6" />
                </div>
              </div>

              {accessCodeError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-600 dark:border-red-800 dark:bg-red-950/50 dark:text-red-300">
                  {accessCodeError}
                </div>
              )}

              <div className="flex flex-col gap-2 pt-1.5 sm:flex-row">
                <button
                  type="button"
                  onClick={() => {
                    setShowAccessCodeModal(false);
                    setAccessCodeInput('');
                    setAccessCodeError('');
                  }}
                  className="order-2 rounded-lg border border-slate-300 px-4 py-2.5 text-slate-600 transition hover:bg-white sm:order-1 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-700"
                  disabled={loading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading}
                  className="order-1 flex-1 rounded-full bg-blue-500 px-4 py-2.5 text-sm sm:text-base font-semibold text-white shadow-sm transition hover:bg-blue-600 disabled:opacity-50 sm:order-2"
                >
                  {loading ? 'Verifying...' : 'Continue to Profile'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showRoleSelectionModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 px-4 py-6 backdrop-blur-sm">
          <div className="w-full max-w-md rounded-3xl border border-slate-200 bg-white p-6 shadow-2xl dark:border-slate-700 dark:bg-slate-800">
            <div className="text-center">
              <div className="mx-auto flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-3xl dark:bg-blue-950/60">
                👋
              </div>

              <h3 className="mt-4 text-2xl font-bold text-slate-900 dark:text-white">
                Welcome to Edsupp
              </h3>

              <p className="mt-2 text-sm leading-relaxed text-slate-600 dark:text-slate-300">
                Your account has been created. Please choose your role to continue.
              </p>
            </div>

            <form onSubmit={handleRoleSelectionSubmit} className="mt-6 space-y-4">
              <label
                className={`flex cursor-pointer items-center gap-4 rounded-2xl border p-4 transition-all ${
                  selectedRole === 'parent'
                    ? 'border-blue-500 bg-blue-50 shadow-sm ring-2 ring-blue-100 dark:bg-blue-950/40 dark:ring-blue-900/40'
                    : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-700 dark:hover:bg-slate-700'
                }`}
              >
                <input
                  type="radio"
                  name="social-role"
                  value="parent"
                  checked={selectedRole === 'parent'}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="sr-only"
                />

                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-2xl shadow-sm dark:bg-slate-700">
                  👨‍👩‍👧
                </div>

                <div className="flex-1">
                  <p className="font-semibold text-slate-900 dark:text-white">Parent</p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Track your child’s learning and progress.
                  </p>
                </div>

                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                    selectedRole === 'parent'
                      ? 'border-blue-600 bg-blue-600'
                      : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-700'
                  }`}
                >
                  {selectedRole === 'parent' && (
                    <span className="h-2 w-2 rounded-full bg-white" />
                  )}
                </span>
              </label>

              <label
                className={`flex cursor-pointer items-center gap-4 rounded-2xl border p-4 transition-all ${
                  selectedRole === 'coaching_teacher'
                    ? 'border-blue-500 bg-blue-50 shadow-sm ring-2 ring-blue-100 dark:bg-blue-950/40 dark:ring-blue-900/40'
                    : 'border-slate-200 bg-white hover:border-blue-300 hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-800 dark:hover:border-blue-700 dark:hover:bg-slate-700'
                }`}
              >
                <input
                  type="radio"
                  name="social-role"
                  value="coaching_teacher"
                  checked={selectedRole === 'coaching_teacher'}
                  onChange={(e) => setSelectedRole(e.target.value)}
                  className="sr-only"
                />

                <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white text-2xl shadow-sm dark:bg-slate-700">
                  👩‍🏫
                </div>

                <div className="flex-1">
                  <p className="font-semibold text-slate-900 dark:text-white">Coaching Teacher</p>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    Create batches and evaluate students easily.
                  </p>
                </div>

                <span
                  className={`flex h-5 w-5 items-center justify-center rounded-full border ${
                    selectedRole === 'coaching_teacher'
                      ? 'border-purple-600 bg-purple-600'
                      : 'border-slate-300 bg-white dark:border-slate-600 dark:bg-slate-700'
                  }`}
                >
                  {selectedRole === 'coaching_teacher' && (
                    <span className="h-2 w-2 rounded-full bg-white" />
                  )}
                </span>
              </label>

              <button
                type="submit"
                disabled={loading || !selectedRole}
                className="mt-2 w-full rounded-2xl bg-blue-600 px-5 py-3 text-base font-semibold text-white shadow-sm transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-slate-300 dark:disabled:bg-slate-700"
              >
                {loading ? 'Saving...' : 'Continue'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AuthPage;
